import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useSendCrewInviteMutation } from "@/store/mover/apis";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { User } from "@/types";
import { SendIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { formatPhoneNumber } from "@/lib/utils";
import CrewMember from "./components/CrewMember";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Crew: React.FC = () => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [sendCrewInviteApi, { isLoading }] = useSendCrewInviteMutation();
  const [newUser, setNewUser] = useState<User>({
    name: "",
    phone: "",
    email: "",
    crew: true,
  });

  const [payRateHourly, setPayRateHourly] = useState("");

  const addCrew = async (data: User) => {
    if (isLoading) {
      return;
    }

    if (!selectedMover) {
      return;
    }

    try {
      const invitedCrew = await sendCrewInviteApi({
        data: {
          user: {
            ...(data.name?.split(" ")[0]
              ? { first_name: data.name.split(" ")[0] }
              : {}),
            ...(data.name?.split(" ")[1]
              ? { last_name: data.name.split(" ").slice(1).join(" ") }
              : {}),
            phone: data.phone,
            ...(data.email ? { email: data.email } : {}),
            crew: true,
            preferred_language: data.preferred_language,
            pay_rate_hourly: parseFloat(payRateHourly),
          },
        },
      }).unwrap();

      dispatch(
        updateSelectedMover({
          ...selectedMover,
          users: [...(selectedMover.users || []), invitedCrew],
        })
      );

      setNewUser({
        name: "",
        phone: "",
        email: "",
        crew: true,
        preferred_language: "English",
      });

      toast({
        title: "Success",
        description: "User added successfully.",
      });
    } catch (error) {
      console.error("Error adding user:", error);
      toast({
        title: "Error",
        description: "Failed to add user. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!selectedMover) {
    return null;
  }

  const sortedCrews = [...selectedMover.users]
    .filter((user) => user.crew)
    .sort((a, b) => {
      return a.archived === b.archived ? 0 : a.archived ? 1 : -1;
    })
    .sort((a, b) => {
      return (
        new Date(a.created_at || "").getTime() -
        new Date(b.created_at || "").getTime()
      );
    });

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold">Crew Accounts</h2>
      <p className="text-base mt-1">
        Add or remove crew members here. For office staff,{" "}
        <Link
          className="font-semibold underline underline-offset-4 decoration-yellow-500 decoration-2"
          to="/mover-settings/users"
        >
          click here
        </Link>
        .
      </p>
      <div className="my-3 bg-amber-100 text-black max-w-lg text-sm p-2 rounded-md space-y-4">
        <p>
          Crew members can access their jobs and timesheets on the mobile app
          and will receive text notifications when they're assigned to a job.
        </p>
        <p>
          <strong>Only the crew lead/foreman</strong> can see the rate and
          billing details of the job. Helpers/movers only see the date, time,
          customer name, and address of the job.
        </p>
        <p>Crews cannot access the office portal.</p>
      </div>
      <div className="mt-4 w-full bg-stone-200 text-black px-2 py-1 text-sm">
        Invite Crew Member
      </div>
      <div className="mt-4 w-full flex flex-col justify-start items-start gap-4">
        <div className="w-full grid grid-cols-12 md:grid-cols-12 gap-4 items-center border border-stone-200 shadow-sm shadow-stone-300 rounded-md md:border-none md:shadow-none md:rounded-none p-4 md:p-0">
          <div className="col-span-12 md:col-span-2">
            <Input
              name="name"
              placeholder="Name"
              value={newUser.name}
              required
              onChange={(e) =>
                setNewUser((prev) => ({ ...prev, name: e.target.value }))
              }
            />
          </div>
          <div className="col-span-12 md:col-span-2">
            <Input
              name="phone"
              placeholder="Phone (for mobile app)"
              value={newUser.phone}
              required
              onChange={(e) =>
                setNewUser((prev) => ({
                  ...prev,
                  phone: formatPhoneNumber(e.target.value),
                }))
              }
            />
          </div>
          <div className="col-span-12 md:col-span-2">
            <Select
              value={newUser.preferred_language}
              onValueChange={(value) =>
                setNewUser((prev) => ({
                  ...prev,
                  preferred_language: value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-12 md:col-span-2">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2">
                $
              </span>
              <Input
                placeholder="Hourly Pay Rate"
                id="payRate"
                type="number"
                step="0.01"
                min="0"
                className="pl-7"
                value={payRateHourly}
                onChange={(e) => setPayRateHourly(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="cursor-pointer col-span-12 md:col-span-6 flex items-center gap-1">
            <Button
              className="w-full md:w-auto border-black px-2 text-black hover:decoration-yellow-500 flex items-center gap-1"
              variant="default"
              type="button"
              onClick={() => {
                if (!newUser.name || !newUser.phone) {
                  alert("Please provide a name and phone number");
                  return;
                }

                addCrew({
                  name: newUser.name,
                  phone: newUser.phone,
                  email: newUser.email || "",
                  crew: true,
                  preferred_language: newUser.preferred_language,
                });
              }}
            >
              <SendIcon className="w-3 h-3" />
              Send Invite
            </Button>
          </div>
        </div>

        <div className="w-full flex flex-col gap-3">
          <div className="bg-stone-200 text-black px-2 py-1 text-sm">
            Active Crew ({sortedCrews.length})
          </div>
          <div className="flex flex-col gap-4 md:gap-3">
            {sortedCrews.map((field) => (
              <CrewMember key={field.id} {...field} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Crew;
